import React from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import bgImage from '../../assets/home/bg-1.jpg';
import {
  HERO_CARDS_SET_1,
  HERO_CARDS_SET_2,
  HERO_CARDS_SET_3,
} from './constants';
import { imageFallback, mirroredCardUrl } from '../../lib/cardImages';

function HeroCard({ src }) {
  return (
    <img
      src={mirroredCardUrl(src)}
      onError={imageFallback([src])}
      alt="Pokemon card"
      className="h-[180px] w-[130px] rounded-lg object-cover shadow-xl sm:h-[220px] sm:w-[160px] lg:h-[280px] lg:w-[200px]"
      loading="lazy"
    />
  );
}

export default function HeroSection() {
  return (
    <section
      style={{ backgroundImage: `url(${bgImage})` }}
      className="relative flex min-h-screen items-start overflow-hidden bg-cover bg-center bg-no-repeat bg-fixed pt-4 lg:items-center"
    >
      <div className="absolute inset-0 z-0 bg-black/70 backdrop-blur-[8px]" />
      <div className="absolute left-1/2 top-1/2 z-0 h-[800px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-primary/20 blur-[120px]" />

      <div className="relative z-10 mx-auto flex w-full max-w-[1500px] flex-col-reverse items-center gap-8 px-4 py-8 sm:px-6 lg:grid lg:grid-cols-2 lg:gap-12 lg:px-12 lg:py-0">
        <div className="relative z-20 mt-4 flex max-w-xl flex-col items-center text-center lg:mt-0 lg:items-start lg:text-left">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-brand-primary/20 bg-brand-primary/10 px-3 py-1.5 text-sm font-medium text-brand-hi">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-hi opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-hi" />
            </span>
            Live Beta Available Now
          </div>

          <h1 className="mb-6 font-landing-display text-4xl font-bold leading-[1.1] tracking-tight sm:text-5xl lg:text-7xl">
            Skyscanner for <br />
            <span className="landing-text-gradient">Pokemon Cards</span>
          </h1>

          <p className="mb-8 px-4 text-base leading-relaxed text-text-secondary sm:text-lg lg:px-0">
            Search once. See every listing from eBay, TCGplayer, and Canadian
            card shops. Compare prices, conditions, and shipping in one place,
            plus price history and collection tracking.
          </p>

          <div className="flex w-full flex-col items-center gap-4 px-4 sm:w-auto sm:flex-row lg:px-0">
            <Link
              to="/series"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary px-8 py-4 font-semibold text-white shadow-[0_0_20px_rgba(108,92,231,0.4)] transition-all hover:bg-brand-hi hover:shadow-[0_0_30px_rgba(108,92,231,0.6)] sm:w-auto"
            >
              <Search size={20} />
              Start Searching (Free)
            </Link>
          </div>
          <p className="mt-4 text-sm text-text-dim">
            No sign-up required — fully functional now
          </p>
        </div>

        <div className="hero-visual relative left-1/2 z-0 mb-6 flex h-[350px] w-[100vw] -translate-x-1/2 items-center justify-center overflow-hidden sm:h-[450px] lg:left-auto lg:mb-0 lg:block lg:h-[800px] lg:w-full lg:translate-x-0 lg:overflow-visible">
          <div className="w-[150%] sm:w-[120%] lg:absolute lg:right-[-45%] lg:top-1/2 lg:w-[160%] lg:-translate-y-1/2">
            <div className="slider-container origin-center rounded-none border-y border-border-base bg-bg-surface/40 p-3 backdrop-blur-xl sm:p-4 lg:origin-left lg:rounded-3xl lg:border lg:p-6">
              <div className="slider-row left mb-3 overflow-hidden rounded-lg sm:mb-4 lg:mb-6 lg:rounded-xl">
                <div className="slider-track">
                  {HERO_CARDS_SET_1.map((src, index) => <HeroCard key={`s1-1-${index}`} src={src} />)}
                  {HERO_CARDS_SET_1.map((src, index) => <HeroCard key={`s1-2-${index}`} src={src} />)}
                </div>
              </div>

              <div className="slider-row right mb-3 overflow-hidden rounded-lg sm:mb-4 lg:mb-6 lg:rounded-xl">
                <div className="slider-track">
                  {HERO_CARDS_SET_2.map((src, index) => <HeroCard key={`s2-1-${index}`} src={src} />)}
                  {HERO_CARDS_SET_2.map((src, index) => <HeroCard key={`s2-2-${index}`} src={src} />)}
                </div>
              </div>

              <div className="slider-row left-fast overflow-hidden rounded-lg lg:rounded-xl">
                <div className="slider-track">
                  {HERO_CARDS_SET_3.map((src, index) => <HeroCard key={`s3-1-${index}`} src={src} />)}
                  {HERO_CARDS_SET_3.map((src, index) => <HeroCard key={`s3-2-${index}`} src={src} />)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
